import Vue from 'vue'
import LogRocket from 'logrocket'
import UserAccess from '../../userAccessLevel'
import {parse, format} from 'date-fns'
import {defaultNominationSettings} from '@/utils/nominations'
import {cacheClassListRows as cacheClassListRowsOnEvent, entryEditForShow} from '@/utils/showClassList'
import {editableEntryIndexes, entryDetailsForIndex, recordEntrySaved} from '@/utils/entryGrouping'
import {entryGroupListRow} from '@/utils/entryGroupListRow'
import {finalizePayload} from '@/utils/preEntries'

const state = {
  adminData: {},
  adminEventID: null,
  adminFor: [],
  userListToken: null,
  logRocketEnabled: false,
  showAccessList: [],
  eventSurvey: null,
  // Who the survey belongs to, when this viewer is a co-admin who can't see
  // it: the emailed link is often opened signed in to the wrong account.
  eventSurveyOwner: null,
  // Not derivable from eventSurvey: null is a real, cacheable answer.
  eventSurveyLoadedFor: null
}

// Deep-linking to the survey page starts the boot fetch and the page's own
// fetch a tick apart; this collapses them.
let pendingEventSurvey = null

// Saves a card's edited entry_details to the given entries of the card. Each entry gets its own
// details with only the fields the user changed (SHO-439), and its row is updated once saved.
function saveCardEntryDetails (rootState, card, entryIDs) {
  return Promise.all(entryIDs.map(e => {
    const entryDetails = entryDetailsForIndex(card, card.entry_ids.indexOf(e), card.entry_details)
    return rootState.apiInstance.put(`/data/entries/${e}`, {entry_details: entryDetails})
      .then(response => {
        recordEntrySaved(card, e, {entry_details: entryDetails})
        return response
      })
  }))
}

// A survey opens three days after the event ends, so an unfinished event
// can't have one and an archived event is past being asked.
function couldHaveSurvey (event) {
  if (event.status === 'ARCHIVED') return false
  const lastDay = event.end_date || event.start_date
  return Boolean(lastDay) && new Date(lastDay) < new Date()
}

const mutations = {
  setLogRocketEnabled: (state, payload) => {
    state.logRocketEnabled = true
  },
  setAdminData: (state, payload) => {
    // Initialize backtag_type based on entry_numbers setting
    if (!payload.event.event_settings.entry_numbers) {
      payload.event.event_settings.backtag_type = 'exhibitor_numbers'
    } else {
      payload.event.event_settings.backtag_type = 'entry_numbers'
    }
    
    // Initialize edit_after_close_mode based on existing boolean settings
    if (!payload.event.entry_settings.edit_after_close_mode) {
      if (payload.event.entry_settings.allow_edit_after_close_strict) {
        payload.event.entry_settings.edit_after_close_mode = 'strict'
      } else if (payload.event.entry_settings.allow_edit_after_close) {
        payload.event.entry_settings.edit_after_close_mode = 'allow'
      } else if (payload.event.entry_settings.use_exhibitor_declared_weights) {
        payload.event.entry_settings.edit_after_close_mode = 'declared_weight_only'
      } else {
        payload.event.entry_settings.edit_after_close_mode = 'none'
      }
    }

    // Nomination Settings: make sure the object and every key exist so the settings form can bind to them
    payload.event.entry_settings.nominations = Object.assign(defaultNominationSettings(), payload.event.entry_settings.nominations || {})
    if (!Array.isArray(payload.event.entry_settings.nominations.open_entry_types)) {
      payload.event.entry_settings.nominations.open_entry_types = []
    }

    state.adminData = payload
  },
  setUserListToken: (state, payload) => {
    state.userListToken = payload.token
  },
  setEventSurvey: (state, payload) => {
    state.eventSurvey = payload
  },
  setEventSurveyOwner: (state, payload) => {
    state.eventSurveyOwner = payload
  },
  setEventSurveyLoadedFor: (state, payload) => {
    state.eventSurveyLoadedFor = payload
  },
  // On sign-out: the cached answer is the old account's, and the next
  // account would be shown it without asking. adminData stays, since the
  // admin pages still mounted read it until the redirect lands.
  resetEventSurvey: (state) => {
    state.eventSurvey = null
    state.eventSurveyOwner = null
    state.eventSurveyLoadedFor = null
    pendingEventSurvey = null
  },
  setAdminFor: (state, payload) => {
    state.adminFor = payload
  },
  setAdminEntryData: (state, payload) => {
    Vue.set(state.adminData, 'entries', payload)
  },
  setAdminEntryReportData: (state, payload) => {
    Vue.set(state.adminData, 'entriesReport', payload)
  },
  updateAdminEvent: (state, payload) => {
    Object.keys(payload).forEach(key => {
      Vue.set(state.adminData.event, key, payload[key])
    })
  },
  updaateAdminEventStatus: (state, payload) => {
    Vue.set(state.adminData.event, 'status', payload.status)
  },
  setExhibitorNumber: (state, payload) => {
    let exhibitor = state.adminData.exhibitors.filter(e => { return e.exhibitor_id === payload.exhibitor_id })[0]
    if (exhibitor) {
      Vue.set(exhibitor, 'exhibitor_number', payload.exhibitor_number)
    }
  },
  setEntryNumber: (state, payload) => {
    let entry = state.adminData.entries.filter(eg => { return eg.entry_id === payload.entry_id })[0]
    Vue.set(entry, 'entry_number', payload.entry_number)
  },
  addEntry_admin: (state, newEntry) => {
    state.adminData.entry_groups.forEach(eg => {
      if (eg.entry_group_id === newEntry.entry_group_id) {
        Vue.set(eg.entries, eg.entries.length, newEntry)
      }
    })
  },
  updateEntry_admin: (state, payload) => {
    // {index: index, type: payload.entryType.toLowerCase(), entry: updatedEntry})
    let entryGroup = state.adminData.entry_groups.filter(eg => { return eg.entry_group_id === payload.entry_group_id })[0]
    Vue.set(entryGroup.entries, payload.index, payload.entry)
  },
  deleteEntry_admin: (state, payload) => {
    let entryGroup = state.adminData.entry_groups.filter(eg => { return eg.entry_group_id === payload.entry_group_id })[0]
    Vue.delete(entryGroup.entries, payload.index)
  },
  addShowToEntry_admin: (state, payload) => {
    let entryGroup = state.adminData.entry_groups.filter(eg => { return eg.entry_group_id === payload.entry_group_id })[0]
    entryGroup.entries[payload.index].shows.push(payload.show)
  },
  removeShowFromEntry_admin: (state, payload) => {
    let entryGroup = state.adminData.entry_groups.filter(eg => { return eg.entry_group_id === payload.entry_group_id })[0]
    Vue.delete(entryGroup.entries[payload.index].shows, entryGroup.entries[payload.index].shows.findIndex((s) => s.show_id === payload.showID || s.value === payload.showID))
  },
  setEntryGroup_admin: (state, payload) => {
    let entryGroupID = payload.entry_group.entry_group_id
    let index = state.adminData.entry_groups.findIndex(eg => eg.entry_group_id === entryGroupID)
    // the list keeps its counts (entryGroupListRow)
    Vue.set(state.adminData.entry_groups, index, entryGroupListRow(payload.entry_group, state.adminData.entry_groups[index]))
  },
  setAnimals_admin: (state, payload) => {
    Vue.set(state.adminData, 'current_animals', payload.animals)
  },
  setInvoiceData_admin: (state, payload) => {
    let entryGroupID = payload.entry_group_id
    let index = state.adminData.entry_groups.findIndex(eg => eg.entry_group_id === entryGroupID)
    Vue.set(state.adminData.entry_groups[index], 'invoice', payload.invoice_data)
  },
  setInvoiceLoading: (state, payload) => {
    let entryGroupID = payload.entry_group_id
    let index = state.adminData.entry_groups.findIndex(eg => eg.entry_group_id === entryGroupID)
    Vue.set(state.adminData.entry_groups[index], 'loading_balance', true)
  },
  // Edit Event Mutators
  adminDataAddShow: (state, payload) => {
    // SHO-582: a just-added show is the request object echoed back, not the DB row. The shows
    // column defaults show_settings to {} so give the client copy the same shape until reload;
    // the Schedule tab reads show_settings.* on every show.
    if (!payload.show_settings) {
      Vue.set(payload, 'show_settings', {})
    }
    state.adminData.event.shows.push(payload)
  },
  adminDataAddEmbeddedShow: (state, { parent_show_id, child }) => {
    const parent = state.adminData.event.shows.find(s => s.show_id === parent_show_id)
    if (parent) {
      if (!parent.embedded_shows) Vue.set(parent, 'embedded_shows', [])
      parent.embedded_shows.push(child)
    }
  },
  adminDataRemoveEmbeddedShow: (state, { parent_show_id, show_id }) => {
    const parent = state.adminData.event.shows.find(s => s.show_id === parent_show_id)
    if (parent && parent.embedded_shows) {
      parent.embedded_shows = parent.embedded_shows.filter(cs => cs.show_id !== show_id)
    }
  },
  adminDataAddPricing: (state, payload) => {
    state.adminData.pricing.push(payload)
  },
  setAdminDataPayments: (state, payload) => {
    Vue.set(state.adminData, 'payments', payload)
  },
  adminDataAddPayment: (state, payload) => {
    state.adminData.payments.push(payload)
  },
  adminDataSetNewShowID: (state, payload) => {
    state.adminData.event.shows[state.adminData.event.shows.length - 1].show_id = payload.show_id
  },
  adminDataRemoveNewShow: (state) => {
    state.adminData.event.shows.pop()
  },
  adminDataSetNewPaymentID: (state, payload) => {
    state.adminData.payments[state.adminData.payments.length - 1].payment_id = payload.payment_id
  },
  adminDataRemoveNewPayment: (state) => {
    state.adminData.payments.pop()
  },
  adminDataRemovePayment: (state, payload) => {
    state.adminData.payments = state.adminData.payments.filter(p => {
      return p.payment_id !== payload.payment_id
    })
  },
  adminDataRemovePricing: (state, payload) => {
    state.adminData.pricing = state.adminData.pricing.filter(p => {
      return p.event_pricing_id !== payload.event_pricing_id
    })
  },
  adminDataSetNewPricingID: (state, payload) => {
    state.adminData.pricing[state.adminData.pricing.length - 1].event_pricing_id = payload.event_pricing_id
  },
  adminDataRemoveShow: (state, payload) => {
    state.adminData.event.shows = state.adminData.event.shows.filter(s => {
      return s.show_id !== payload.show_id
    })
  },
  adminDataUpdateShow: (state, payload) => {
    state.adminData.event.shows.forEach(s => {
      if (s.show_id === payload.show_id) {
        s = payload
      }
    })
  },
  adminDataUpdatePricing: (state, payload) => {
    state.adminData.pricing.forEach(p => {
      if (p.event_pricing_id === payload.event_pricing_id) {
        p = payload
      }
    })
  },
  adminDataCoverPhotoURL: (state, payload) => {
    state.adminData.event.coverPhotoURL = payload.url
  },
  adminDataUpdateFile: (state, payload) => {
    // TODO: Make file an object with name keys to URL
    // state.adminData.event.files[payload.name] = payload.url
    state.adminData.event.files = [payload]
  },
  setEventPricing: (state, payload) => {
    state.pricing = payload
  },
  setCheckInComplete: (state, payload) => {
    let index = state.adminData.entry_groups.findIndex(eg => eg.entry_group_id === payload.entry_group_id)
    Vue.set(state.adminData.entry_groups[index], 'checkin_complete', payload.complete)
    // state.adminData.entry_groups[index].checkin_complete = payload.complete
  },
  setMemberStatus: (state, payload) => {
    let index = state.adminData.entry_groups.findIndex(eg => eg.entry_group_id === payload.entry_group_id)
    state.adminData.entry_groups[index].member_pricing = payload.member_pricing
  },
  setDateEntered: (state, payload) => {
    let index = state.adminData.entry_groups.findIndex(eg => eg.entry_group_id === payload.entry_group_id)
    state.adminData.entry_groups[index].date_entered = payload.date_entered
  },
  // Sponsors
  addSponsor: (state, payload) => {
    state.adminData.event.sponsors.push(payload)
  },
  removeSponsor: (state, payload) => {
    Vue.delete(state.adminData.event.sponsors, payload.index)
  },
  // For Addiong Show Dynamic
  addEventShowType: (state, payload) => {
    state.adminData.event_show_types.push(payload)
  },
  addClassConfig: (state, payload) => {
    state.adminData.class_configurations.push(payload)
  },
  // Exhibitors
  // Commented out on 4/28
  addToExhibitorList_admin: (state, exhibitor) => {
    state.adminData.exhibitors.push(exhibitor)
  },
  updateExhibitorInList_admin: (state, exhibitor) => {
    state.adminData.exhibitors.forEach(e => {
      if (e.exhibitor_id === exhibitor.exhibitor_id) {
        Vue.set(e, 'entry_details', exhibitor.exhibitor_details)
      }
    })
  },
  /* New Generic Mutators  */
  adminDataAddData: (state, payload) => {
    state.adminData[payload.name].push(payload)
  },
  adminDataSetNewDataID: (state, payload) => {
    state.adminData[payload.name][state.adminData[payload.name].length - 1][payload.idName] = payload.id
  },
  adminDataUpdateData: (state, payload) => {
    state.adminData[payload.name].forEach((d, i) => {
      if (d[payload.idName] === payload[payload.idName]) {
        Vue.set(state.adminData[payload.name], i, payload)
      }
    })
  },
  // Files
  adminDataUpdateFiles: (state, files) => {
    state.adminData.event.files = files
  },
  // Entry Questions
  adminDataAddEntryQuestion: (state, entryQuestion) => {
    state.adminData.event.entry_questions.push(entryQuestion)
  },
  adminDataUpdateEntryQuestion: (state, entryQuestion) => {
    state.adminData.event.entry_questions.forEach(eq => {
      if (eq.entry_question_id === entryQuestion.entry_question_id) {
        eq = entryQuestion
      }
    })
  },
  adminDataRemoveEntryQuestion: (state, entryQuestion) => {
    state.adminData.event.entry_questions = state.adminData.event.entry_questions.filter(eq => {
      return eq.entry_question_id !== entryQuestion.entry_question_id
    })
  },
  // Users Access
  setAdminUserAccess: (state, userAccess) => {
    state.adminData.event_user_access = userAccess
  },
  addAdminUserAccess: (state, userAccess) => {
    state.adminData.event_user_access.push(userAccess)
  },
  removeAdminUserAccess: (state, toDelete) => {
    state.adminData.event_user_access = state.adminData.event_user_access.filter(item => item.id !== toDelete.id)
  },
  setEntryQuestionReservations_admin: (state, payload) => {
    let eq = state.adminData.event.entry_questions.filter(eq => eq.entry_question_id === payload.entry_question_id)[0]
    Vue.set(eq.details, 'number_reserved', payload.reserved)
  },
  setEventEntryQuestions_admin: (state, payload) => {
    // state.currentEvent.entry_questions = payload
    Vue.set(state.adminData.event, 'entry_questions', payload)
  },
  updateEventSettings_admin: (state, payload) => {
    Vue.set(state.adminData.event.event_settings, payload.field, payload.value)
  },
  updateShowAccessList: (state, payload) => {
    state.adminData.event_user_access.forEach(ua => {
      if (ua.user_id === payload.user_id) {
        if (ua.all_show_access) {
          let list = []
          state.adminData.event.shows.forEach(s => {
            list.push(s.show_id)
          })
          Vue.set(state, 'showAccessList', list)
        } else {
          Vue.set(state, 'showAccessList', ua.show_access_list)
        }
      }
    })
  }
}

const actions = {
  loadAdminData: ({commit, state, rootState, dispatch, getters}, payload) => {
    return rootState.apiInstance.get(`/callFunction/adminData?userID=${getters.getUserID}`)
      .then(response => {
        commit('setAdminFor', response.data.data.adminFor)
        commit('appInitiated')
      })
  },
  loadAdminEventData: ({commit, state, rootState, dispatch, getters}, payload) => {
    if (process.env.NODE_ENV !== 'development') {
      if (!state.logRocketEnabled) {
        LogRocket.init('kzaivo/showman')
        commit('setLogRocketEnabled')
        LogRocket.identify(rootState.user.user_id, {
          email: rootState.user.email,
          name: `${rootState.user.first_name} ${rootState.user.last_name}`
        })
      }
    }
    return rootState.apiInstance.get(`/getAdminData?event_id=${payload.event_slug}${payload.year ? `&year=${payload.year}` : ''}`)
      .then(response => {
        commit('setAdminData', response.data.data)
        dispatch('getUserListToken', {})
        commit('updateShowAccessList', {user_id: getters.getUserID})
      })
  },
  // Feeds the banner, the overview notice and the form. AdminHome asks on
  // every entry into the admin area, but the answer only moves when the event
  // does or when this tab writes, and writes commit their own response — so
  // fetch once per event, and leave the marker unset on failure to retry.
  loadEventSurvey: ({commit, state, rootState}, payload) => {
    if (state.eventSurveyLoadedFor === payload.event_id) {
      return Promise.resolve(state.eventSurvey)
    }
    if (pendingEventSurvey && pendingEventSurvey.eventID === payload.event_id) {
      return pendingEventSurvey.request
    }

    // Or the old event's banner shows against the new one.
    commit('setEventSurvey', null)
    commit('setEventSurveyOwner', null)
    commit('setEventSurveyLoadedFor', null)

    // Asked before the dashboard payload landed; don't cache that as a miss.
    const event = state.adminData.event
    if (!event) return Promise.resolve(null)

    // Skipping costs no request, so there's nothing to cache — and leaving
    // the marker unset is what lets the survey page ask for itself with
    // `force`, which the emailed link needs on an archived event.
    if (!payload.force && !couldHaveSurvey(event)) return Promise.resolve(null)

    const request = rootState.railsAPI.get('/admin/event_survey')
      .then(response => {
        // They moved to another event while this was loading; its survey
        // would show on the new event's dashboard.
        if (!state.adminData.event || state.adminData.event.event_id !== payload.event_id) return null
        // Signed out while this was loading; the answer was for that account.
        if (!pendingEventSurvey || pendingEventSurvey.request !== request) return null
        commit('setEventSurvey', response.data.event_survey)
        commit('setEventSurveyOwner', response.data.survey_owner || null)
        commit('setEventSurveyLoadedFor', payload.event_id)
        return state.eventSurvey
      })
      .finally(() => {
        // Only our own: after a sign-out the next account's may already be here.
        if (pendingEventSurvey && pendingEventSurvey.request === request) {
          pendingEventSurvey = null
        }
      })

    pendingEventSurvey = {eventID: payload.event_id, request}
    return request
  },
  // Autosave for a single answer; never finishes the survey.
  saveEventSurveyAnswer: ({commit, rootState}, payload) => {
    const body = {recommendation: payload.recommendation, reasons: payload.reasons}
    // Omitted on questions that rate nobody, so the server leaves it alone.
    if (payload.share_with_specialist !== null && payload.share_with_specialist !== undefined) {
      body.share_with_specialist = payload.share_with_specialist
    }
    return rootState.railsAPI.patch(`/admin/event_survey/questions/${payload.question_key}`, body)
      .then(response => commit('setEventSurvey', response.data.event_survey))
  },
  submitEventSurvey: ({commit, rootState}, payload) => {
    return rootState.railsAPI.post('/admin/event_survey/submit', {})
      .then(response => commit('setEventSurvey', response.data.event_survey))
  },
  getUserListToken: ({commit, state, rootState, getters}, payload) => {
    return rootState.apiInstance.get(`/userlist/getUserListToken?id=${getters.adminEventID}`)
      .then(response => {
        commit('setUserListToken', {token: response.data.token})
      })
  },
  loadAdminEntryData: ({commit, state, rootState, dispatch, getters}, payload) => {
    let url = `/callFunction/allEntiresReporting?event_id=${payload.event_id}`
    if (payload.include_deleted_entries) {
      url += '&include_deleted_entries=true'
    }
    return rootState.apiInstance.get(url)
      .then(response => {
        commit('setAdminEntryData', response.data.data.entries)
      })
  },
  loadAdminEntryReportData: ({commit, state, rootState, dispatch, getters}, payload) => {
    let url = `/callFunction/allEntiresReportingNew?event_id=${payload.event_id}`
    if (payload.include_deleted_entries) {
      url += '&include_deleted_entries=true'
    }
    return rootState.apiInstance.get(url)
      .then(response => {
        commit('setAdminEntryReportData', response.data.data.entries)
      })
  },
  // Admin Event Actions
  createNewShow: ({commit, state, getters, rootState}, newShow) => {
    // Judges are now managed separately through the show_judges table
    // Remove judge/judge_id from payload
    const showPayload = {...newShow}
    delete showPayload.judge
    delete showPayload.judge_id
    delete showPayload.judges

    showPayload.event_id = getters.adminEventID
    showPayload.admin_id = state.adminData.event.admin_id
    showPayload.created_by = getters.getUserID

    commit('adminDataAddShow', showPayload)

    return rootState.apiInstance.post(`/data/shows/`, showPayload)
      .then(response => {
        if (response.status === 200) {
          commit('adminDataSetNewShowID', {show_id: response.data.data.show_id})
        } else {
          commit('adminDataRemoveNewShow')
        }
      })
  },
  removeShow: ({commit, rootState}, payload) => {
    return rootState.apiInstance.delete(`/data/shows/${payload.show_id}`)
      .then(response => {
        if (response.status === 200) {
          commit('adminDataRemoveShow', payload)
        } else {
          return Promise.reject(new Error('Show Not Deleted'))
        }
      })
  },
  updateEvent: ({commit, rootState}, payload) => {
    return rootState.apiInstance.put(`/data/events/${payload.event_id}`, payload)
      .then(response => {
        if (response.status === 200) {
          commit('updateAdminEvent', payload)
        }
      })
  },
  updateEventStatus ({commit, rootState}, payload) {
    return rootState.apiInstance.put(`/data/events/${payload.event_id}`, payload)
      .then(response => {
        if (response.status === 200) {
          commit('updaateAdminEventStatus', payload)
        }
      })
  },
  updateShow: ({commit, rootState}, payload) => {
    // Judges are now managed separately through the show_judges table
    // Remove judge/judge_id from payload to avoid conflicts
    const updatePayload = {...payload}
    delete updatePayload.judge
    delete updatePayload.judge_id
    delete updatePayload.judges // Don't send judges array to show update
    // The show's class list is only written by setShowClassList (an explicit change). Sending the
    // loaded value on every save would let a stale page undo a DB trigger's move (SHO-408).
    delete updatePayload.show_class_list_id

    return rootState.apiInstance.put(`/data/shows/${payload.show_id}`, updatePayload)
      .then(response => {
        if (response.status === 200) {
          commit('adminDataUpdateShow', payload)
        }
      })
  },
  // Point a show at a class list (SHO-408). Sends only show_class_list_id, then updates the show
  // object in place (the Edit Show modal edits the store's own show object).
  setShowClassList: ({rootState}, payload) => {
    const show = payload.show
    const classListId = payload.show_class_list_id
    return rootState.apiInstance.put(`/data/shows/${show.show_id}`, {show_id: show.show_id, show_class_list_id: classListId})
      .then(response => {
        if (response.status === 200) {
          Vue.set(show, 'show_class_list_id', classListId)
        }
        return response
      })
  },
  // Move a show to another entry type (SHO-429). Sends only entry_details_id -- the show keeps its
  // class list (the DB fill trigger never overwrites a set one); switch it with setShowClassList.
  setShowEntryType: ({rootState}, payload) => {
    const show = payload.show
    const entryTypeId = payload.entry_details_id
    return rootState.apiInstance.put(`/data/shows/${show.show_id}`, {show_id: show.show_id, entry_details_id: entryTypeId})
      .then(response => {
        if (response.status === 200) {
          Vue.set(show, 'entry_details_id', entryTypeId)
        }
        return response
      })
  },
  // Put a class list's rows where the per-show readers look (SHO-408)
  cacheClassListRows: ({state}, {listId, rows}) => {
    cacheClassListRowsOnEvent(state.adminData && state.adminData.event, listId, rows)
  },
  // Fetch a class list's rows and cache them (SHO-408)
  loadClassListRows: ({dispatch, rootState}, listId) => {
    if (!listId) return Promise.resolve([])
    return rootState.apiInstance.get(`/classListRows/${listId}`)
      .then(response => {
        const rows = (response.data && response.data.data) ? response.data.data : []
        dispatch('cacheClassListRows', {listId, rows})
        return rows
      })
  },
  createNewPricing: ({commit, state, getters, rootState}, pricingRule) => {
    pricingRule.admin_id = state.adminData.event.admin_id
    pricingRule.created_by = getters.getUserID
    pricingRule.event_id = getters.adminEventID

    commit('adminDataAddPricing', pricingRule)

    return rootState.apiInstance.post('/data/eventPricing', pricingRule)
      .then(response => {
        if (response.status === 200) {
          commit('adminDataSetNewPricingID', {event_pricing_id: response.data.data.event_pricing_id})
        } else {
          commit('adminDataRemovePricing')
          return Promise.reject(new Error('Pricing rule not added.'))
        }
      })
      .catch(error => {
        commit('adminDataRemovePricing')
        return Promise.reject(error)
      })
  },
  updatePricing: ({commit, rootState}, pricingRule) => {
    return rootState.apiInstance.put(`/data/eventPricing/${pricingRule.event_pricing_id}`, pricingRule)
      .then(response => {
        if (response.status === 200) {
          commit('adminDataUpdatePricing', pricingRule)
        } else {
          return Promise.reject(new Error('Pricing rule not updated.'))
        }
      })
  },
  removePricing: ({commit, rootState}, pricingRule) => {
    return rootState.apiInstance.delete(`/data/eventPricing/${pricingRule.event_pricing_id}`)
      .then(response => {
        if (response.status === 200) {
          commit('adminDataRemovePricing', pricingRule)
        } else {
          return Promise.reject(new Error('Rule not deleted'))
        }
      })
  },
  uploadEventCoverPhoto: ({dispatch, commit, state, rootState, getters}, payload) => {
    return rootState.apiInstance.post('/image-upload', payload, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
      .then(response => {
        if (response.status === 200) {
          commit('adminDataSetCoverPhotoURL', {url: response.data.imageUrl})
          dispatch('updateEvent', {eventID: getters.adminEventID, coverPhotoURL: response.data.imageUrl})
        }
      })
  },
  uploadEventFile: ({commit, dispatch, state, rootState, getters}, payload) => {
    return rootState.apiInstance.post('/image-upload', payload.file, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
      .then(response => {
        if (response.status === 200) {
          var file = {
            name: payload.name,
            url: response.data.imageUrl
          }
          // TODO: Update how files are stored
          commit('adminDataUpdateFile', file)
          dispatch('updateEvent', {eventID: getters.adminEventID, files: JSON.stringify(state.adminData.event.files)})
        }
      })
  },

  // Load Admin Payments
  loadInAdminPayments: ({commit}, payments) => {
    commit('setAdminDataPayments', payments)
  },
  // Credits/Payments
  addEntryCredit: ({commit, dispatch, state, getters, rootState}, entryCredit) => {
    entryCredit.admin_id = state.adminData.event.admin_id
    entryCredit.total_amount = entryCredit.show_amount
    entryCredit.created_by = getters.getUserID
    entryCredit.event_id = getters.adminEventID
    entryCredit.type = 'CREDIT'
    entryCredit.status = 'PAID'
    commit('adminDataAddPayment', entryCredit)
    return rootState.apiInstance.post('/data/payments', {
      admin_id: state.adminData.event.admin_id,
      show_amount: entryCredit.show_amount * 100,
      total_amount: entryCredit.show_amount * 100,
      created_by: getters.getUserID,
      event_id: getters.adminEventID,
      entry_group_id: entryCredit.entry_group_id,
      user_id: entryCredit.user_id,
      type: 'CREDIT',
      status: 'PAID',
      date_paid: entryCredit.date_paid,
      email: entryCredit.email,
      details: entryCredit.details
    })
      .then(response => {
        if (response.status === 200) {
          commit('adminDataSetNewPaymentID', {payment_id: response.data.data.payment_id})
          if (!entryCredit.date_entered && entryCredit.entry_group_id) {
            dispatch('setEntryDate_admin', {entry_group_id: entryCredit.entry_group_id, date_entered: new Date(entryCredit.date_paid)})
          }
          if (!entryCredit.entry_group_id) {
            rootState.apiInstance.get(`/callFunction/checkCredits?event_id=${getters.adminEventID}`)
          }
        } else {
          commit('adminDataRemoveNewPayment')
          return Promise.reject(new Error('Credit not added.'))
        }
      })
      .catch(error => {
        commit('adminDataRemoveNewPayment')
        return Promise.reject(error)
      })
  },
  removeCredit: ({commit, rootState}, payment) => {
    return rootState.apiInstance.delete(`/data/payments/${payment.payment_id}`)
      .then(response => {
        if (response.status === 200) {
          commit('adminDataRemovePayment', payment)
        } else {
          return Promise.reject(new Error('Rule not deleted'))
        }
      })
  },
  refundCharge: ({commit, rootState}, chargeDetails) => {
    return rootState.apiInstance.post(`/stripe/refundCharge?`, chargeDetails)
      .then(response => {
        if (response.status === 200) {
          return Promise.resolve(response)
        } else {
          return Promise.reject(new Error('Error issuing refund'))
        }
      })
  },
  // Exhibitor Number
  checkExhibitorNumber: ({commit, rootState}, payload) => {
    return rootState.apiInstance.post('/checkExhibitorNumber', {exhibitor_id: payload.exhibitor_id, event_id: payload.event_id})
  },
  // Edit Entry Admin Functions
  createEntry_admin_: ({commit, state, getters, rootState, dispatch}, payload) => {
    // Quick Fix for Adding In the Exhibitor
    payload.newEntry.exhibitor_id = payload.newEntry.exhibitor.exhibitor_id
    // TODO: FIx this monstrosity
    // let originalEntryType = payload.entry_type
    let split = payload.entry_type.split('_')[0]
    if (split === 'SHOWMANSHIP') {
      payload.entry_type = split
    }

    // payload.newEntry.entry_group_id = state.currentEntryGroup.entry_group_id
    payload.newEntry.admin_id = state.adminData.event.admin_id
    // payload.newEntry.user_id = getters.getUserID
    payload.newEntry.entry_type = payload.entry_type
    payload.newEntry.created_by = getters.getUserID

    var newEntry = JSON.parse(JSON.stringify(payload.newEntry))

    // commit('addEntry', {type: payload.entry_type.toLowerCase(), newEntry: newEntry})
    // commit('setLastAddedEntry_admin', newEntry)

    return rootState.apiInstance.post('/addEntries', payload.newEntry)
      .then(response => {
        // Don't need this for the admin part
        // Maybe change to updateInvoiceData
        // dispatch('getInvoiceData', {entry_group_id: payload.entry_group_id})
        var entryIDs = []
        response.data.data.forEach(d => {
          entryIDs.push(d.entry_id)
        })
        newEntry.entry_ids = entryIDs
        // commit('addEntry_admin', newEntry)
        // dispatch('getInvoiceData_admin', {entry_group_id: payload.entry_group_id})
        dispatch('updateEntryGroup_admin', {entry_group_id: payload.entry_group_id})
        return Promise.resolve(response)
      })
      .catch(error => {
        // var index = state.currentEntryGroup.entries[newEntry.entryType.toLowerCase()].findIndex(e => e.name === newEntry.name)
        // commit('deleteEntry', {type: newEntry.entryType.toLowerCase(), index: index})
        return Promise.reject(error)
      })
  },
  createEntry_admin: ({commit, state, getters, rootState, dispatch}, payload) => {
    payload.newEntry.admin_id = state.adminData.event.admin_id
    payload.newEntry.created_by = getters.getUserID
    payload.newEntry.exhibitor_id = payload.newEntry.exhibitor.exhibitor_id
    payload.newEntry.animal_id = payload.newEntry.animal ? payload.newEntry.animal.animal_id : null
    payload.newEntry.is_admin = true

    var newEntry = JSON.parse(JSON.stringify(payload.newEntry))
    var showList = []
    newEntry.shows.forEach(s => {
      showList.push(s.value)
    })
    newEntry.shows = showList

    // commit('addEntry', {newEntry: newEntry})
    // commit('setLastAddedEntry', newEntry)

    return rootState.apiInstance.post('/addEntries', payload.newEntry)
      .then(response => {
        var entryIDs = []
        response.data.data.forEach(d => {
          entryIDs.push(d.entry_id)
        })
        newEntry.entry_ids = entryIDs
        // commit('addEntry_admin', newEntry)
        // dispatch('getInvoiceData_admin', {entry_group_id: payload.entry_group_id})
        // same animal in another class: the saved animal keeps its own details. The group reloads
        // after the animal is saved: a new animal's entries only get its animal_id then, and the
        // cards need it to tell when this animal is entered again
        // SHO-549: a pre-entry has no animal yet
        const animalSaved = payload.keepSavedAnimal || payload.newEntry.pre_entry_count ? Promise.resolve() : dispatch('saveAnimal', {exhibitor_id: payload.newEntry.exhibitor_id, animal: payload.newEntry.animal, entry_ids: entryIDs, admin: true})
        animalSaved
          .catch(() => {})
          .then(() => dispatch('updateEntryGroup_admin', {entry_group_id: payload.entry_group_id}))
        return Promise.resolve(response)
      })
      .catch(error => {
        // var index = state.currentEntryGroup.entries[newEntry.entryType.toLowerCase()].findIndex(e => e.name === newEntry.name)
        // commit('deleteEntry', {index: index})
        return Promise.reject(error)
      })
  },
  editEntry_admin_: ({commit, state, getters, rootState, dispatch}, payload) => {
    // console.log(payload)
    // TODO: Add all Edit Entry Logic To the Database??
    // TODO: Implement optimistic updates in UI so I don't have to rely on full refresh
    var promises = []
    var promisesTypes = []
    // TODO: Change this to be consistent with CreateEntry
    // let originalEntryType = payload.entryType
    let split = payload.entryType.split('_')[0]
    if (split === 'SHOWMANSHIP') {
      payload.entryType = split
    }

    var index = state.adminData.entry_groups.filter(eg => { return eg.entry_group_id === payload.entry_group_id })[0].entries.findIndex(e => e.entry_ids[0] === payload.updateEntry.entry_ids[0])
    // commit('setLastEditedEntry', state.currentEntryGroup.entries[payload.entryType.toLowerCase()][index])

    if (payload.type === 'Class' || payload.type === 'Entry Details') {
      // SHO-439: shows already checked in on a card whose shows differ are left as they are
      editableEntryIndexes(payload.updateEntry).forEach(i => {
        const e = payload.updateEntry.entry_ids[i]
        // SHO-409: each entry keeps its own show's class list (class + division per show)
        const perShow = entryEditForShow(payload.updateEntry, i)
        // promises.push(rootState.apiInstance.put(`/data/entries/${e}`, {
        promises.push(rootState.apiInstance.put(`/editEntries/${e}`, {
          entry_details: perShow.entry_details,
          class_id: perShow.class_id,
          exhibitor_id: payload.updateEntry.exhibitor.exhibitor_id
        }))
        // promises.push(Promise.reject(new Error('Fake error for test')))
      })
      var updatedEntry = JSON.parse(JSON.stringify(payload.updateEntry))
      updatedEntry.entry_type = payload.entryType
      updatedEntry.class_id = payload.updateEntry.class_id
      updatedEntry.class_number = payload.updateEntry.class_number
      updatedEntry.class_name = payload.updateEntry.class_name
      updatedEntry.exhibitor_id = payload.updateEntry.exhibitor.exhibitor_id
      // commit('updateEntry', {index: index, type: payload.entryType.toLowerCase(), entry: updatedEntry})
    } else if (payload.type === 'Shows') {
      // Check if new Show
      let showsToAdd = [] // payload.updateEntry.shows.filter(s => { return s.show_id })
      payload.updateEntry.shows.forEach(s => {
        if (payload.updateEntry.originalShows.indexOf(s.value) === -1) {
          // Then is a new show
          showsToAdd.push(s)
        }
      })
      if (showsToAdd.length) {
        var newEntry = {
          class_id: payload.updateEntry.class_id,
          // SHO-409: added shows on another class list carry their own class
          class_ids_by_show: payload.updateEntry.class_ids_by_show,
          division_by_show: payload.updateEntry.division_by_show,
          entry_details: payload.updateEntry.entry_details,
          shows: showsToAdd,
          created_by: getters.getUserID,
          user_id: payload.updateEntry.user_id,
          exhibitor_id: payload.updateEntry.exhibitor.exhibitor_id
        }
        newEntry.admin_id = state.adminData.event.admin_id
        payload.newEntry.admin_id = state.adminData.event.admin_id

        // payload.newEntry.user_id = getters.getUserID
        newEntry.entry_group_id = payload.entry_group_id // state.currentEntryGroup.entry_group_id
        newEntry.entry_type = payload.entryType
        newEntry.class_name = payload.updateEntry.class_name

        if (payload.entryType === 'BREEDING') {
          newEntry.goat_id = payload.updateEntry.entry_details.goat_id
        }

        showsToAdd.forEach(s => {
          commit('addShowToEntry_admin', {index: index, show: s, entry_group_id: payload.entry_group_id})
        })
        promises.push(rootState.apiInstance.post(`/addEntries/`, newEntry))
        promisesTypes.push('add')
      }
      // Check for deleted show
      payload.updateEntry.originalShows.forEach((s, i) => {
        if (!payload.updateEntry.shows.filter(show => { return show.value === s }).length) {
          // Then is a deleted show
          // TODO: Need to update logic to be from backend
          let showIDToRemove = s
          let entry = state.adminData.entry_groups.filter(eg => { return eg.entry_group_id === payload.entry_group_id })[0].entries[index]
          let entryIDToRemove = entry.show_entries.entry_ids[entry.show_entries.show_ids.findIndex(id => id === showIDToRemove)]

          // Requires shows and entries being sorted the same way by show_id
          promises.push(rootState.apiInstance.delete(`/data/entries/${entryIDToRemove}`))
          promisesTypes.push('delete')

          commit('removeShowFromEntry_admin', {index: index, showID: s, entry_group_id: payload.entry_group_id, shows: payload.updateEntry.shows})
        }
      })
    }
    return Promise.all(promises)
      .then(response => {
        dispatch('updateEntryGroup_admin', {entry_group_id: payload.entry_group_id})
      })
      .catch(error => {
        // commit('updateEntry', {index: index, type: payload.entryType.toLowerCase(), entry: state.lastEditedEntry})
        return Promise.reject(error)
      })
  },
  // SHO-549: as entryGroup's finalizePreEntry, for the admin popup and check-in table
  finalizePreEntry_admin: ({rootState, dispatch}, payload) => {
    const entry = payload.entry
    return rootState.apiInstance.post('/finalizePreEntry', finalizePayload(entry))
      .then(response => {
        const entryIDs = response.data.data.map(d => d.entry_id)
        const animalSaved = payload.keepSavedAnimal ? Promise.resolve() : dispatch('saveAnimal', {exhibitor_id: finalizePayload(entry).exhibitor_id, animal: entry.animal, entry_ids: entryIDs, admin: true})
        animalSaved
          .catch(() => {})
          .then(() => dispatch('updateEntryGroup_admin', {entry_group_id: payload.entry_group_id}))
        return response
      })
  },
  editEntry_admin: ({commit, state, getters, rootState, dispatch}, payload) => {
    var promises = []
    var promisesTypes = []

    var index = state.adminData.entry_groups.filter(eg => { return eg.entry_group_id === payload.entry_group_id })[0].entries.findIndex(e => e.entry_ids[0] === payload.updateEntry.entry_ids[0])
    // commit('setLastEditedEntry', state.currentEntryGroup.entries[index])

    if (payload.type === 'Class' || payload.type === 'Entry Details') {
      // SHO-439: shows already checked in on a card whose shows differ are left as they are
      editableEntryIndexes(payload.updateEntry).forEach(i => {
        const e = payload.updateEntry.entry_ids[i]
        // SHO-409: each entry keeps its own show's class list (class + division per show)
        const perShow = entryEditForShow(payload.updateEntry, i)
        promises.push(rootState.apiInstance.put(`/editEntries/${e}`, {
          entry_details: perShow.entry_details,
          class_id: perShow.class_id,
          exhibitor_id: payload.updateEntry.exhibitor.exhibitor_id,
          created_at: new Date(payload.updateEntry.created_at).toISOString(),
          is_admin: true
        }))
      })
      var updatedEntry = JSON.parse(JSON.stringify(payload.updateEntry))
      var showList = []
      updatedEntry.shows.forEach(s => {
        showList.push(s.value)
      })
      updatedEntry.shows = showList

      // commit('updateEntry', {index: index, entry: updatedEntry})
    } else if (payload.type === 'Shows') {
      var showsToAdd = []
      // Check if new Show
      payload.updateEntry.shows.forEach(s => {
        if (payload.updateEntry.originalShows.indexOf(s.value) === -1) {
          // Then is a new show
          showsToAdd.push(s)
        }
      })

      if (showsToAdd.length) {
        var newEntry = {
          class_id: payload.updateEntry.class_id,
          // SHO-409: added shows on another class list carry their own class
          class_ids_by_show: payload.updateEntry.class_ids_by_show,
          division_by_show: payload.updateEntry.division_by_show,
          entry_details: payload.updateEntry.entry_details,
          shows: showsToAdd,
          created_by: getters.getUserID,
          user_id: payload.updateEntry.user_id,
          exhibitor_id: payload.updateEntry.exhibitor.exhibitor_id,
          animal_id: payload.updateEntry.animal ? payload.updateEntry.animal.animal_id : null,
          is_admin: true
        }
        // newEntry.user_id = state.currentEntryGroup.user_id
        newEntry.entry_group_id = payload.entry_group_id
        newEntry.admin_id = state.adminData.event.admin_id
        newEntry.entry_type = payload.updateEntry.entry_type

        // Don't copy place
        newEntry.entry_details.place = null

        promises.push(rootState.apiInstance.post(`/addEntries/`, newEntry))
        promisesTypes.push('add')
      }

      // Check for deleted show
      payload.updateEntry.originalShows.forEach((s, i) => {
        if (!payload.updateEntry.shows.filter(show => { return show.value === s }).length) {
          promises.push(rootState.apiInstance.delete(`/data/entries/${payload.updateEntry.entry_ids[i]}`))
          promisesTypes.push('delete')
          // commit('removeShowFromEntry', {index: index, showID: s})
        }
      })
    }
    return Promise.all(promises)
      .then(response => {
        if (payload.updateEntry.animal) {
          dispatch('saveAnimal', {exhibitor_id: payload.updateEntry.exhibitor.exhibitor_id, animal: payload.updateEntry.animal, entry_ids: payload.updateEntry.entry_ids, admin: true})
        }
        dispatch('updateEntryGroup_admin', {entry_group_id: payload.entry_group_id})
      })
      .catch(error => {
        commit('updateEntry', {index: index, entry: state.lastEditedEntry})
        return Promise.reject(error)
      })
  },
  deleteEntry_admin: ({commit, state, getters, rootState, dispatch}, payload) => {
    var promises = []
    payload.entry.entry_ids.forEach(e => {
      promises.push(rootState.apiInstance.delete(`/data/entries/${e}`))
    })
    return Promise.all(promises)
      .then(response => {
        // commit('deleteEntry_admin', {entry_group_id: payload.entry_group_id, index: index})
        dispatch('updateEntryGroup_admin', {entry_group_id: payload.entry_group_id})
      })
      .catch(error => {
        return Promise.reject(error)
      })
  },
  deleteIndividualEntry_admin: ({commit, state, gettes, rootState, dispatch}, payload) => {
    return rootState.apiInstance.delete(`/data/entries/${payload.entry_id}`)
      .then(response => {
        // commit('deleteEntry_admin', {entry_group_id: payload.entry_group_id, index: index})
        dispatch('updateEntryGroup_admin', {entry_group_id: payload.entry_group_id})
      })
      .catch(error => {
        return Promise.reject(error)
      })
  },
  updateEntryGroup_admin: ({commit, state, getters, rootState, dispatch}, payload) => {
    return rootState.apiInstance.get(`/callFunction/adminGetEntryGroup?entry_group_id=${payload.entry_group_id}`)
      .then(response => {
        // Edited on 8/25 to fix entry grouping
        let entryGroup = Vue.prototype.fixEntryGrouping(response.data.data.entry_group)
        // convert Dates to local correct format
        entryGroup.entries.forEach(e => {
          e.created_at = format(parse(e.created_at), 'YYYY-MM-DD hh:mm:ss')
          if (e.last_updated_at) {
            e.last_updated_at = format(parse(e.last_updated_at), 'YYYY-MM-DD hh:mm:ss')
          }
          if (e.checked_in) {
            e.checked_in = format(parse(e.checked_in), 'YYYY-MM-DD hh:mm:ss')
          }
        })
        commit('setEntryGroup_admin', {entry_group: entryGroup})
        commit('setAnimals_admin', {animals: response.data.data.animals})
        dispatch('getInvoiceData_admin', {entry_group_id: payload.entry_group_id})
        return entryGroup
      })
  },
  getInvoiceData_admin: ({commit, state, getters, rootState, dispatch}, payload) => {
    commit('setInvoiceLoading', {entry_group_id: payload.entry_group_id})
    return rootState.apiInstance.get(`/invoice?entry_group_id=${payload.entry_group_id}`)
      .then(response => {
        commit('setInvoiceData_admin', {invoice_data: response.data.data, entry_group_id: payload.entry_group_id})
        return response.data.data
      })
  },
  checkInExhibitor: ({commit, state, rootState, dispatch}, payload) => {
    return rootState.apiInstance.put(`/data/entryGroups/${payload.entry_group_id}`, {checkin_complete: payload.complete})
      .then(response => {
        commit('setCheckInComplete', payload)
      })
  },
  updateMemberStatus: ({commit, state, rootState, dispatch}, payload) => {
    return rootState.apiInstance.put(`/data/entryGroups/${payload.entry_group_id}`, {member_pricing: payload.member_pricing})
      .then(response => {
        commit('setMemberStatus', payload)
        dispatch('getInvoiceData_admin', {entry_group_id: payload.entry_group_id})
      })
  },
  updateEntryExhibitor: ({commit, rootState}, payload) => {
    return saveCardEntryDetails(rootState, payload.updateEntry, payload.updateEntry.entry_ids)
  },
  updateEntryWeight: ({commit, rootState}, payload) => {
    // An entry has one row per show - updateEntry.entry_ids is parallel to updateEntry.shows.
    // Omit limit_to_entry_ids to write every row (the normal case); pass a subset to write only
    // those rows, e.g. a superintendent updating just the shows they have access to.
    return saveCardEntryDetails(rootState, payload.updateEntry, payload.limit_to_entry_ids || payload.updateEntry.entry_ids)
  },
  updateEntryClassification: ({commit, rootState}, payload) => {
    return saveCardEntryDetails(rootState, payload.updateEntry, payload.updateEntry.entry_ids)
  },
  setCheckInEntry: ({commit, rootState}, payload) => {
    let promises = []
    // An entry has one row per show - updateEntry.entry_ids is parallel to updateEntry.shows.
    // Omit limit_to_entry_ids to check in every row (the normal case); pass a subset to check in
    // only those rows, e.g. a superintendent checking in just the shows they have access to.
    // payload.checked_in, when given, is the value for just these rows (one show of a card whose
    // shows differ, SHO-439); otherwise the card's own checked_in, as before.
    let entryIDs = payload.limit_to_entry_ids || payload.updateEntry.entry_ids
    const checkedIn = 'checked_in' in payload ? payload.checked_in : payload.updateEntry.checked_in
    entryIDs.forEach(e => {
      const saved = () => recordEntrySaved(payload.updateEntry, e, {checked_in: checkedIn})
      if (checkedIn) {
        promises.push(rootState.apiInstance.put(`/data/entries/${e}?update_timestamp=false`, {
          checked_in: checkedIn,
          checked_in_by: rootState.user.user_id,
          checked_out_by: null,
          checked_out_at: null
        }).then(saved))
      } else {
        promises.push(rootState.apiInstance.put(`/data/entries/${e}?update_timestamp=false`, {
          checked_in: checkedIn,
          // checked_in_by: null,
          checked_out_at: new Date(),
          checked_out_by: rootState.user.user_id,
          checked_in_by: null
        }).then(saved))
      }
    })
    return Promise.all(promises)
  },
  generateMarketClassList: ({rootState}, payload) => {
    return rootState.apiInstance.get(`/createMarketClassListNew?event_id=${payload.event_id}&show_id=${payload.show_id}`)
  },
  generateShowBook: ({rootState}, payload) => {
    return rootState.apiInstance.get(`/generateShowBook?show_id=${payload.show_id}&preview=${payload.preview}`)
  },
  saveShowBook: ({rootState}, payload) => {
    // // console.log('Makre sure you replace this code\nDont forget about the bred and owned debacle')
    return rootState.apiInstance.post('/callFunction/saveShowBook', {showBookData: JSON.stringify(payload)})
  },
  saveShowBookManual: ({rootState}, payload) => {
    /* event_id: this.adminData.event.event_id,
    show_id: this.show_book.show_id,
    show_book: this.breakEntries,
    user_id: this.currentUser.user_id */
    return rootState.apiInstance.post('/saveShowBookManual', payload)
  },
  getShowBook: ({rootState}, payload) => {
    if (payload.ring_id) {
      return rootState.apiInstance.get(`/getShowBook?show_id=${payload.show_id}&ring_id=${payload.ring_id}&preview=${payload.preview}&regen=${payload.regen}&breakable=${payload.breakable}&results=${payload.results}&start_date=${payload.start_date}&use_dynamic=${payload.use_dynamic}&no_ring_filter=${payload.no_ring_filter}&add_class_numbers=${payload.add_class_numbers}&testMode=${payload.testMode}&structure_only=${payload.structure_only}`)
    } else {
      return rootState.apiInstance.get(`/getShowBook?show_id=${payload.show_id}&preview=${payload.preview}&regen=${payload.regen}&breakable=${payload.breakable}&results=${payload.results}&start_date=${payload.start_date}&use_dynamic=${payload.use_dynamic}&no_ring_filter=${payload.no_ring_filter}&add_class_numbers=${payload.add_class_numbers}&testMode=${payload.testMode}&structure_only=${payload.structure_only}`)
    }
  },
  saveEntryPlacing: ({rootState}, payload) => {
    return rootState.apiInstance.put(`/data/entries/${payload.entry_id}`, {entry_details: payload.entry_details})
  },
  saveEntryScore: ({rootState}, payload) => {
    return rootState.apiInstance.put(`/data/entries/${payload.entry_id}`, {entry_details: payload.entry_details})
  },
  saveEntryRibbon: ({rootState}, payload) => {
    return rootState.apiInstance.put(`/data/entries/${payload.entry_id}`, {entry_details: payload.entry_details})
  },
  saveShowChampionResults: ({rootState}, payload) => {
    return rootState.apiInstance.put(`/data/shows/${payload.show_id}`, {champion_results: payload.champion_results})
  },
  upsertShowResult: ({rootState}, payload) => {
    return rootState.apiInstance.post('/callFunction/upsertShowResult', {resultData: JSON.stringify(payload)})
  },
  upsertDynamicChampionResult: ({rootState}, payload) => {
    return rootState.apiInstance.post('/callFunction/upsertDynamicChampionResult', {resultData: JSON.stringify(payload)})
  },
  deleteChampionResult: ({rootState}, payload) => {
    return rootState.apiInstance.post('/callFunction/deleteChampionResult', {resultData: JSON.stringify(payload)})
  },
  deleteDynamicChampionResult: ({rootState}, payload) => {
    return rootState.apiInstance.post('/callFunction/deleteDynamicChampionResult', {resultData: JSON.stringify(payload)})
  },
  saveEntryQuestions_admin: ({rootState, dispatch}, payload) => {
    return rootState.apiInstance.put(`/data/entryGroups/${payload.entry_group_id}`, {entry_questions: payload.entry_questions})
      .then(response => {
        dispatch('updateEntryGroup_admin', {entry_group_id: payload.entry_group_id})
        // dispatch('getInvoiceData_admin', {entry_group_id: payload.entry_group_id})
      })
      .catch(error => {
        return Promise.reject(error)
      })
  },
  saveSponsor: ({rootState, state, commit, getters}, sponsor) => {
    sponsor.admin_id = state.adminData.event.admin_id
    if (sponsor.sponsor_id) {
      sponsor.last_updated_by = getters.getUserID
      return rootState.apiInstance.put(`/data/sponsors/${sponsor.sponsor_id}`, sponsor)
    } else {
      sponsor.created_by = getters.getUserID
      return rootState.apiInstance.post('/data/sponsors', sponsor)
        .then(result => {
          sponsor.sponsor_id = result.data.data.sponsor_id
          commit('addSponsor', sponsor)
        })
    }
  },
  deleteSponsor: ({rootState, commit}, payload) => {
    return rootState.apiInstance.delete(`/data/sponsors/${payload.sponsor_id}`)
      .then(result => {
        commit('removeSponsor', payload)
      })
  },
  setEntryDate_admin: ({rootState, commit, dispatch}, payload) => {
    return rootState.apiInstance.put(`/data/entryGroups/${payload.entry_group_id}`, {date_entered: payload.date_entered})
      .then(response => {
        commit('setDateEntered', payload)
        dispatch('getInvoiceData_admin', {entry_group_id: payload.entry_group_id})
      })
  },
  // Walk-in & Mail-In
  checkEmail: ({rootState}, payload) => {
    return rootState.apiInstance.get(`/callFunction/checkEmail?exhibitorEmail=${payload.email}`)
  },
  createWalkInExhibitor: ({rootState, dispatch}, payload) => {
    return rootState.apiInstance.post(`/callFunction/newWalkInExhibitor`, {exhibitorData: JSON.stringify(payload)})
      .then(response => {
        return dispatch('loadAdminEventData', {event_slug: payload.event_slug})
          .then(() => {
            return Promise.resolve(response)
          })
      })
  },
  deleteEntryGroup: ({rootState, dispatch}, payload) => {
    return rootState.apiInstance.post('/callFunction/deleteEntryGroup', {entry_group_id: payload.entry_group_id})
      .then(response => {
        dispatch('loadAdminEventData', {event_slug: payload.event_slug})
        return Promise.resolve(response)
      })
  },

  // New Entry Questions
  createNewEntryQuestion: ({commit, state, getters, rootState}, entryQuestion) => {
    entryQuestion.admin_id = state.adminData.event.admin_id
    entryQuestion.created_by = getters.getUserID
    entryQuestion.event_id = getters.adminEventID

    // TODO: Implement Eager Saving
    return rootState.apiInstance.post('/data/entryQuestions', entryQuestion)
      .then(response => {
        if (response.status === 200) {
          entryQuestion.entry_question_id = response.data.data.entry_question_id
          commit('adminDataAddEntryQuestion', entryQuestion)
        } else {
          return Promise.reject(new Error('Entry Question not added.'))
        }
      })
      .catch(error => {
        return Promise.reject(error)
      })
  },
  updateEntryQuestion: ({commit, rootState}, entryQuestion) => {
    return rootState.apiInstance.put(`/data/entryQuestions/${entryQuestion.entry_question_id}`, entryQuestion)
      .then(response => {
        if (response.status === 200) {
          commit('adminDataUpdateEntryQuestion', entryQuestion)
        } else {
          return Promise.reject(new Error('Entry Question not updated.'))
        }
      })
  },
  removeEntryQuestion: ({commit, rootState}, entryQuestion) => {
    return rootState.apiInstance.delete(`/data/entryQuestions/${entryQuestion.entry_question_id}`)
      .then(response => {
        if (response.status === 200) {
          commit('adminDataRemoveEntryQuestion', entryQuestion)
        } else {
          return Promise.reject(new Error('Question not deleted'))
        }
      })
  },

  previewClassList: ({commit, rootState}, payload) => {
    return rootState.apiInstance.post('/previewClassList', payload)
      .then(response => {
        if (response.status < 400) {
          return response.data.data
        } else {
          return Promise.reject(new Error('Data not added'))
        }
      })
      .catch(error => {
        return Promise.reject(error)
      })
  },
  previewEntryType: ({commit, rootState}, payload) => {
    return rootState.apiInstance.post('/previewEntryType', payload)
      .then(response => {
        if (response.status < 400) {
          return response.data.data
        } else {
          return Promise.reject(new Error('Data not added'))
        }
      })
      .catch(error => {
        return Promise.reject(error)
      })
  },
  addShow: ({commit, rootState}, payload) => {
    return rootState.apiInstance.post('/addShow', payload)
      .then(response => {
        if (response.status < 400) {
          return response.data.data
        } else {
          return Promise.reject(new Error('Data not added'))
        }
      })
      .catch(error => {
        return Promise.reject(error)
      })
  },
  addEmbeddedShow: ({commit, rootState}, payload) => {
    return rootState.apiInstance.post('/addEmbeddedShow', payload)
      .then(response => {
        if (response.status === 200) {
          commit('adminDataAddEmbeddedShow', { parent_show_id: payload.parent_show_id, child: response.data.data })
          return response.data.data
        } else {
          return Promise.reject(new Error('Embedded show not created'))
        }
      })
  },
  removeEmbeddedShow: ({commit, rootState}, { show_id, parent_show_id }) => {
    return rootState.apiInstance.delete(`/data/shows/${show_id}`)
      .then(response => {
        if (response.status === 200) {
          commit('adminDataRemoveEmbeddedShow', { parent_show_id, show_id })
        } else {
          return Promise.reject(new Error('Embedded show not deleted'))
        }
      })
  },
  addShowDynamic: ({commit, rootState}, payload) => {
    return rootState.apiInstance.post('/addShowDynamic', payload)
      .then(response => {
        if (response.status < 400) {
          if (!payload.classConfig.class_configuration_id) {
            commit('addClassConfig', response.data.data.class_list)
          }
          if (!payload.entryType.event_show_type_id) {
            commit('addEventShowType', response.data.data.entry_type)
          }
          let show = response.data.data.show
          show.show_id = response.data.data.show_id
          if (!show.pricing_details) {
            show.pricing_details = {}
          }
          commit('adminDataAddShow', show)

          return response.data.data
        } else {
          return Promise.reject(new Error('Data not added'))
        }
      })
      .catch(error => {
        return Promise.reject(error)
      })
  },
  addShowDynamicTemplate ({commit, rootState}, payload) {
    return rootState.apiInstance.post('addShowDynamicTemplate', payload)
      .then(response => {
        if (response.status < 400) {
          if (!payload.classConfig.class_configuration_id) {
            commit('addClassConfig', response.data.data.class_list)
          }
          if (!payload.entryType.event_show_type_id) {
            commit('addEventShowType', response.data.data.entry_type)
          }
          let shows = response.data.data.shows
          shows.forEach(show => {
            if (!show.pricing_details) {
              show.pricing_details = {}
            }
            commit('adminDataAddShow', show)
          })
          return response.data.data
        } else {
          return Promise.reject(new Error('Data not added'))
        }
      })
      .catch(error => {
        return Promise.reject(error)
      })
  },
  // New Generic Actions
  addData: ({commit, rootState}, payload) => {
    payload.idName = payload.name.slice(0, -1) + '_id'

    payload.event_id = state.adminData.event.event_id
    payload.admin_id = state.adminData.event.admin_id
    payload.created_by = rootState.user.user_id

    commit('adminDataAddData', payload)
    return rootState.apiInstance.post(`/data/${payload.name}/`, payload)
      .then(response => {
        if (response.status < 400) {
          payload.id = response.data.data[payload.idName]
          commit('adminDataSetNewDataID', payload)
          return response
        } else {
          commit('adminDataRemoveNewData', payload)
          return Promise.reject(new Error('Data not added'))
        }
      })
      .catch(() => {
        commit('adminDataRemoveNewData', payload)
      })
  },
  updateData: ({commit, rootState}, payload) => {
    payload.idName = payload.name.slice(0, -1) + '_id'
    return rootState.apiInstance.put(`/data/${payload.name}/${payload[payload.idName]}`, payload)
      .then(response => {
        if (response.status === 200) {
          commit('adminDataUpdateData', payload)
        } else {
          return Promise.reject(new Error('Update Failed'))
        }
      })
  },
  deleteData: ({commit, rootState}, payload) => {
    payload.idName = payload.name.slice(0, -1) + '_id'

    return rootState.apiInstance.delete(`/data/${payload.name}/${[payload[payload.idName]]}`)
      .then(response => {
        if (response.status === 200) {
          commit('adminDataRemoveData', payload)
        } else {
          return Promise.reject(new Error('Data not deleted'))
        }
      })
  },
  updateShowSettings: ({commit, rootState}, payload) => {
    return rootState.apiInstance.post(`/callFunction/updateShowSettings`, {updateData: JSON.stringify(payload)})
      .then(response => {
        if (response.status === 200) {
          return Promise.resolve(response)
        } else {
          return Promise.reject(new Error('Show not updated'))
        }
      })
  },
  addExhibitorToAdminData: ({commit}, exhibitor) => {
    commit('addToExhibitorList_admin', exhibitor)
  },
  // File Functions
  updateFiles: ({commit, rootState}, payload) => {
    return rootState.apiInstance.put(`/data/events/${payload.event_id}`, {files: payload.files})
      .then(response => {
        if (response.status === 200) {
          commit('adminDataUpdateFiles', payload.files)
        } else {
          return Promise.reject(new Error('Update Failed'))
        }
      })
  },
  updateExhibitorNumber: ({commit, rootState}, object) => {
    // update AdminData
    commit('setExhibitorNumber', object)
    return rootState.apiInstance.post(`/updateExhibitorNumber`, object)
      .catch(error => {
        // re_update adminData
        object.exhibitor_number = object.originalNumber
        commit('setExhibitorNumber', object)
        return Promise.reject(error)
      })
  },
  checkEventEntryQuestionAvailability_admin ({commit, state, rootState}, payload) {
    return rootState.apiInstance.get(`/checkEntryQuestionAvailability?event_id=${state.adminData.event.event_id}&entry_question_id=${payload.entry_question_id}`)
      .then(response => {
        commit('setEntryQuestionReservations_admin', {reserved: response.data.reserved, entry_question_id: payload.entry_question_id})
        return response.data.reserved
      })
  },
  getEntryQuestions_admin: ({commit, state, rootState}, payload) => {
    return rootState.apiInstance.get(`/getUpdatedEntryQuestions?event_id=${state.adminData.event.event_id}`)
      .then(response => {
        commit('setEventEntryQuestions_admin', response.data)
        return response.data
      })
  },
  setEventSettings_admin: ({commit, state, rootState}, payload) => {
    return rootState.apiInstance.post(`/callFunction/updateEventSettings`, {
      updateData: JSON.stringify(payload)
    })
      .then(response => {
        if (response.status === 200) {
          commit('updateEventSettings_admin', payload)
          return Promise.resolve(response)
        } else {
          return Promise.reject(new Error('Settings not updated'))
        }
      })
  },
  setShowAccessList: ({commit, state, rootState}, payload) => {
    commit('updateShowAccessList', payload)
  }
}

const getters = {
  // Whether this event can have a live survey. Gates the fetch, and gates the
  // banner and overview notice too: the survey page can force a fetch past
  // this, and its result must not then surface on the dashboard.
  eventCouldHaveSurvey: state => Boolean(state.adminData.event) && couldHaveSurvey(state.adminData.event),
  adminEventID: state => {
    if (state.adminData.event) {
      return state.adminData.event.event_id
    } else {
      return null
    }
  },
  getEventUserRoles: state => {
    if (state.adminData) {
      return state.adminData.event_user_roles
    } else {
      return []
    }
  },
  getAccessFilteredShowList: (state, getters) => {
    let userID = getters.getUserID
    const allShows = state.adminData.event.shows.filter(s => !s.parent_show_id)
    if (userID === state.adminData.admin_id || UserAccess.checkUserAccess(userID, 'SUPPORT') || !state.adminData.event.event_settings.superintendent_access) {
      return allShows
    }
    return allShows.filter(s => state.showAccessList.includes(s.show_id))
  },
  getAccessFilteredShowBooks: (state, getters) => {
    let userID = getters.getUserID
    const childShowIds = new Set(
      (state.adminData.event.shows || []).filter(s => s.parent_show_id).map(s => s.show_id)
    )
    const allBooks = (state.adminData.show_books || []).filter(s => !childShowIds.has(s.show_id))
    if (userID === state.adminData.admin_id || UserAccess.checkUserAccess(userID, 'SUPPORT') || !state.adminData.event.event_settings.superintendent_access) {
      return allBooks
    }
    return allBooks.filter(s => state.showAccessList.includes(s.show_id))
  },
  // True when the current user's view of this event should be limited to the shows
  // they were given superintendent access to.
  isShowAccessScoped: (state, getters) => {
    const event = state.adminData ? state.adminData.event : null
    if (!event || !event.event_settings || !event.event_settings.superintendent_access) {
      return false
    }
    let userID = getters.getUserID
    if (userID === state.adminData.admin_id || UserAccess.checkUserAccess(userID, 'SUPPORT')) {
      return false
    }
    // Show access is stored on the event_user_access row. A user who reached this event some other
    // way (series admin, internal ADMIN role) has no row and therefore no show list to scope to.
    let userAccess = (state.adminData.event_user_access || []).find(ua => ua.user_id === userID)
    if (!userAccess || userAccess.all_show_access) {
      return false
    }
    return true
  },
  // The show_ids the current user may act on, or null when they are not scoped at all.
  // null means "no filtering" - it is deliberately not the full list of show_ids so that
  // consumers never accidentally filter out a show that is missing from event.shows.
  getAccessFilteredShowIds: (state, getters) => {
    if (!getters.isShowAccessScoped) {
      return null
    }
    return Array.isArray(state.showAccessList) ? [...state.showAccessList] : []
  }
}
export default {
  namespaced: false,
  state,
  actions,
  mutations,
  getters
}
